import * as THREE from "three";
import { loadModel, type LoadedModel } from "../assets/loader.js";
import type { ZoneView } from "./api.js";
import { CELL, cellToWorld } from "./grid.js";

/** Collects the meshes of a template with their world matrices relative to the template root. */
function templateMeshes(
  model: LoadedModel,
): { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[]; matrix: THREE.Matrix4 }[] {
  const out: {
    geometry: THREE.BufferGeometry;
    material: THREE.Material | THREE.Material[];
    matrix: THREE.Matrix4;
  }[] = [];
  model.template.updateMatrixWorld(true);
  const rootInverse = model.template.matrixWorld.clone().invert();
  model.template.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    out.push({
      geometry: mesh.geometry,
      material: mesh.material,
      matrix: rootInverse.clone().multiply(mesh.matrixWorld),
    });
  });
  return out;
}

/** One InstancedMesh per template mesh: cheap repetition of a small model (wall segments, rubble tiles). */
export class InstancedModel {
  readonly group = new THREE.Group();
  private meshes: THREE.InstancedMesh[] = [];
  private parts: ReturnType<typeof templateMeshes> = [];
  private transforms: THREE.Matrix4[] = [];
  private colors: readonly THREE.Color[] = [];
  private model: LoadedModel | null = null;
  private disposed = false;

  constructor(
    private readonly id: string,
    private readonly variant?: number | string,
    lod: 0 | 1 = 0,
  ) {
    this.group.name = this.id;
    void loadModel(id, variant, lod).then((m) => {
      if (this.disposed) return;
      this.model = m;
      this.parts = templateMeshes(m);
      this.rebuild();
    }).catch(() => {
      if (!this.disposed) this.group.userData["loadError"] = true;
    });
  }

  /** Replaces every instance transform (matrices relative to the group). */
  setTransforms(transforms: THREE.Matrix4[], colors: readonly THREE.Color[] = []): void {
    this.transforms = transforms;
    this.colors = colors;
    this.rebuild();
  }

  private rebuild(): void {
    for (const m of this.meshes) {
      this.group.remove(m);
      m.dispose();
    }
    this.meshes = [];
    if (!this.model || this.transforms.length === 0) return;
    const tmp = new THREE.Matrix4();
    for (const part of this.parts) {
      const inst = new THREE.InstancedMesh(part.geometry, part.material, this.transforms.length);
      inst.castShadow = this.transforms.length <= 200;
      inst.receiveShadow = true;
      this.transforms.forEach((t, i) => {
        inst.setMatrixAt(i, tmp.copy(t).multiply(part.matrix));
        if (this.colors[i]) inst.setColorAt(i, this.colors[i]!);
      });
      inst.instanceMatrix.needsUpdate = true;
      this.group.add(inst);
      this.meshes.push(inst);
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const m of this.meshes) m.dispose();
    this.meshes = [];
  }
}

const cellKey = (x: number, y: number) => `${x},${y}`;

/** The Bastion Wall follows the outer edge of every open cell; segments are 1 m long along X (R13, R17). */
export class WallRig {
  private inst: InstancedModel | null = null;
  private tier = 0;
  private signature = "";
  readonly group = new THREE.Group();

  update(openCells: Set<string>, tier: 1 | 2 | 3 | 0, size: number): void {
    const signature = `${tier}|${openCells.size}|${[...openCells].sort().join(";")}`;
    if (signature === this.signature) return;
    this.signature = signature;
    if (tier !== this.tier) {
      this.inst?.dispose();
      if (this.inst) this.group.remove(this.inst.group);
      this.inst = null;
      this.tier = tier;
      if (tier > 0) {
        this.inst = new InstancedModel("bld.bastion_wall", tier);
        this.group.add(this.inst.group);
      }
    }
    if (!this.inst) return;
    const transforms: THREE.Matrix4[] = [];
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3(1, 1, 1);
    const p = new THREE.Vector3();
    const half = size / 2;
    for (const key of openCells) {
      const [x, y] = key.split(",").map(Number) as [number, number];
      // Four edges; a segment sits on an edge that borders a non-open cell. Rotation faces the glow strip outward.
      const edges: [number, number, number, number, number][] = [
        [x, y - 1, x + 0.5, y, 0], // north edge (−z)
        [x, y + 1, x + 0.5, y + 1, Math.PI], // south edge (+z)
        [x - 1, y, x, y + 0.5, Math.PI / 2], // west edge (−x)
        [x + 1, y, x + 1, y + 0.5, -Math.PI / 2], // east edge (+x)
      ];
      for (const [nx, ny, ex, ey, rot] of edges) {
        if (openCells.has(cellKey(nx, ny))) continue;
        p.set((ex - half) * CELL, 0.02, (ey - half) * CELL);
        q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rot);
        transforms.push(new THREE.Matrix4().compose(p, q, s));
      }
    }
    this.inst.setTransforms(transforms);
  }

  dispose(): void {
    this.inst?.dispose();
  }
}

/** Locked zones show ash rubble tiles and ruins; reclaiming zones add a pulsing holo ring (R13). */
export class ZoneRig {
  readonly group = new THREE.Group();
  private ruins: InstancedModel;
  private overlays = new Map<string, THREE.Mesh>();
  private rings = new Map<string, THREE.Mesh>();
  private signature = "";
  private readonly overlayMat = new THREE.MeshStandardMaterial({
    color: 0x676749,
    transparent: true,
    opacity: 0.12,
    roughness: 1,
    metalness: 0,
    depthWrite: false,
  });
  private readonly ringMat = new THREE.MeshStandardMaterial({
    color: 0x06222a,
    emissive: new THREE.Color(0x22d3ee),
    emissiveIntensity: 2,
    transparent: true,
    opacity: 0.85,
  });

  constructor() {
    this.ruins = new InstancedModel("ruin", undefined, 1);
    this.group.add(this.ruins.group);
  }

  /** `lowDetail` (quality low / software rendering) keeps only the overlay and ruins (R92 budget). */
  update(zones: ZoneView[], size: number, lowDetail = false): void {
    const signature = `${lowDetail ? "L" : "H"}|` + zones.map((z) => `${z.id}:${z.state}`).join("|");
    if (signature === this.signature) return;
    this.signature = signature;
    const ruinTransforms: THREE.Matrix4[] = [];
    const q = new THREE.Quaternion();
    const up = new THREE.Vector3(0, 1, 0);
    for (const mesh of [...this.overlays.values(), ...this.rings.values()]) {
      this.group.remove(mesh);
      mesh.geometry.dispose();
    }
    this.overlays.clear();
    this.rings.clear();
    for (const zone of zones) {
      if (zone.state === "open") continue;
      const r = zone.rect;
      let seed = zone.id.length * 17;
      const rnd = () => ((seed = (seed * 9301 + 49297) % 233280), seed / 233280);
      for (let y = r.y; y < r.y + r.h; y++) {
        for (let x = r.x; x < r.x + r.w; x++) {
          const { wx, wz } = cellToWorld(x, y, 1, 1, size);
          if (rnd() < (lowDetail ? 0.035 : 0.05)) {
            q.setFromAxisAngle(up, rnd() * Math.PI * 2);
            ruinTransforms.push(
              new THREE.Matrix4().compose(
                new THREE.Vector3(wx, 0.05, wz),
                q,
                new THREE.Vector3(0.9, 0.9, 0.9),
              ),
            );
          }
        }
      }
      const centre = cellToWorld(r.x, r.y, r.w, r.h, size);
      const overlay = new THREE.Mesh(new THREE.PlaneGeometry(r.w * CELL, r.h * CELL), this.overlayMat);
      overlay.rotation.x = -Math.PI / 2;
      overlay.position.set(centre.wx, 0.025, centre.wz);
      this.group.add(overlay);
      this.overlays.set(zone.id, overlay);
      if (zone.state === "reclaiming") {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(Math.min(r.w, r.h) * 0.4, 0.06, 8, 48),
          this.ringMat,
        );
        ring.rotation.x = Math.PI / 2;
        ring.position.set(centre.wx, 0.4, centre.wz);
        this.group.add(ring);
        this.rings.set(zone.id, ring);
      }
    }
    this.ruins.setTransforms(ruinTransforms);
  }

  tick(time: number): void {
    const pulse = 0.5 + 0.5 * Math.sin(time * 3);
    this.ringMat.emissiveIntensity = 1.2 + pulse * 1.8;
    for (const ring of this.rings.values()) ring.position.y = 0.4 + pulse * 0.25;
  }

  dispose(): void {
    for (const mesh of [...this.overlays.values(), ...this.rings.values()]) mesh.geometry.dispose();
    this.ruins.dispose();
    this.overlayMat.dispose();
    this.ringMat.dispose();
  }
}

/** Pulsing cyan holo ring around a building under construction or upgrade (R12). */
export function makeHoloRing(w: number, h: number): THREE.Mesh {
  const radius = Math.max(w, h) * 0.5 * CELL + 0.25;
  const mat = new THREE.MeshStandardMaterial({
    color: 0x06222a,
    emissive: new THREE.Color(0x22d3ee),
    emissiveIntensity: 2.2,
    transparent: true,
    opacity: 0.9,
  });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.05, 8, 48), mat);
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.3;
  ring.name = "holo";
  return ring;
}

/** Short particle burst on completion: emissive gold shards flying outward under gravity (R93 juice). */
export class Burst {
  readonly mesh: THREE.InstancedMesh;
  private readonly velocities: THREE.Vector3[] = [];
  private readonly positions: THREE.Vector3[] = [];
  private life = 0;
  done = false;
  private static geometry = new THREE.OctahedronGeometry(0.09, 0);
  private static material = new THREE.MeshStandardMaterial({
    color: 0x3a2a00,
    emissive: new THREE.Color(0xf5c542),
    emissiveIntensity: 3,
  });

  constructor(origin: THREE.Vector3, count = 28) {
    this.mesh = new THREE.InstancedMesh(Burst.geometry, Burst.material, count);
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.3;
      const speed = 2.5 + Math.random() * 2.5;
      this.velocities.push(
        new THREE.Vector3(Math.cos(a) * speed, 4 + Math.random() * 3, Math.sin(a) * speed),
      );
      this.positions.push(origin.clone());
    }
    this.apply();
  }

  private apply(): void {
    const m = new THREE.Matrix4();
    const scale = Math.max(0.001, 1 - this.life / 1.3);
    this.positions.forEach((p, i) => {
      m.makeScale(scale, scale, scale).setPosition(p);
      this.mesh.setMatrixAt(i, m);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  tick(dt: number): void {
    this.life += dt;
    for (let i = 0; i < this.positions.length; i++) {
      const v = this.velocities[i]!;
      v.y -= 12 * dt;
      this.positions[i]!.addScaledVector(v, dt);
    }
    this.apply();
    if (this.life > 1.3) this.done = true;
  }

  dispose(): void {
    this.mesh.dispose();
  }
}
